import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import services from 'services';
import WelcomeModal from './WelcomeModal';
import { TOUR_ROUTES, isPatientProfilePath, isShowAppointmentPath, } from './constants';
import { canAccessTour, getAvailableTours, getFinancialTourRoute, getInventoryTourRoute, } from './permissions';
import appConfig from 'config/app';
import { isTourComplete, isWelcomeComplete, markTourComplete, markWelcomeComplete, clearAllOnboarding, clearTourCompletion, setPendingTour, consumePendingTour, peekPendingTour, } from './storage';
import { buildTourSteps } from './tours';
import { runTour } from './runTour';
import { OnboardingContext } from './context';
var getTourRoute = function (tourId) {
    var _a;
    if (tourId === 'patientProfile' || tourId === 'patientLog') {
        return null;
    }
    if (tourId === 'inventory') {
        return getInventoryTourRoute();
    }
    if (tourId === 'financial') {
        return getFinancialTourRoute();
    }
    return (_a = TOUR_ROUTES[tourId]) !== null && _a !== void 0 ? _a : null;
};
var getTourForPath = function (pathname) {
    if (isPatientProfilePath(pathname) && canAccessTour('patientProfile')) {
        return 'patientProfile';
    }
    if (pathname === '/reservation-packages' && canAccessTour('reservation')) {
        return 'reservation';
    }
    if (pathname === '/packages' && canAccessTour('package')) {
        return 'package';
    }
    if (pathname === '/patients' && canAccessTour('patientHistory')) {
        return 'patientHistory';
    }
    if (pathname === '/appointments' && canAccessTour('appointment')) {
        return 'appointment';
    }
    if (isShowAppointmentPath(pathname) && canAccessTour('patientLog')) {
        return 'patientLog';
    }
    if (pathname === getInventoryTourRoute() &&
        canAccessTour('inventory')) {
        return 'inventory';
    }
    if (pathname === getFinancialTourRoute() &&
        canAccessTour('financial')) {
        return 'financial';
    }
    return null;
};
var getFirstPendingTour = function (userId) {
    var available = getAvailableTours();
    for (var _i = 0, available_1 = available; _i < available_1.length; _i++) {
        var tourId = available_1[_i];
        if (isTourComplete(userId, tourId)) {
            continue;
        }
        var route = getTourRoute(tourId);
        if (!route) {
            continue;
        }
        return { tourId: tourId, route: route };
    }
    return null;
};
var OnboardingProvider = function (_a) {
    var children = _a.children;
    var t = useTranslation('onboarding').t;
    var location = useLocation();
    var navigate = useNavigate();
    var userService = services.userService;
    var _b = userService.useCurrentUser(), currentUser = _b.data, isLoading = _b.isLoading;
    var permissionsLoaded = userService.useUserPermissions(currentUser).isSuccess;
    var _c = useState(false), showWelcome = _c[0], setShowWelcome = _c[1];
    var tourRunningRef = useRef(false);
    var welcomeCheckedRef = useRef(false);
    var chainToursRef = useRef(false);
    var userId = currentUser === null || currentUser === void 0 ? void 0 : currentUser.getId();
    var tourLabels = useMemo(function () { return ({
        next: t('controls.next'),
        prev: t('controls.previous'),
        done: t('controls.done'),
        progress: t('controls.progress'),
    }); }, [t]);
    var startTour = useCallback(function (tourId, options) {
        var _a;
        if (!userId || tourRunningRef.current) {
            return;
        }
        if (!(options === null || options === void 0 ? void 0 : options.force) && isTourComplete(userId, tourId)) {
            return;
        }
        chainToursRef.current = (_a = options === null || options === void 0 ? void 0 : options.chain) !== null && _a !== void 0 ? _a : false;
        tourRunningRef.current = true;
        var steps = buildTourSteps(tourId, t);
        runTour(steps, tourLabels, function () {
            markTourComplete(userId, tourId);
            tourRunningRef.current = false;
            if (tourId === 'patientHistory' && canAccessTour('patientProfile')) {
                setPendingTour('patientProfile');
                message.info(t('patientHistory.openPatientHint'));
            }
            if (tourId === 'appointment' && canAccessTour('patientLog')) {
                setPendingTour('patientLog');
                message.info(t('appointment.openAppointmentHint'));
            }
            if (!chainToursRef.current) {
                return;
            }
            var nextPending = getFirstPendingTour(userId);
            if (!nextPending) {
                return;
            }
            if (location.pathname === nextPending.route) {
                window.setTimeout(function () { return startTour(nextPending.tourId, { chain: true, force: true }); }, 600);
                return;
            }
            navigate(nextPending.route);
        });
    }, [location.pathname, navigate, t, tourLabels, userId]);
    var replayTour = useCallback(function (tourId) {
        if (!userId || !canAccessTour(tourId)) {
            return;
        }
        clearTourCompletion(userId, tourId);
        chainToursRef.current = false;
        if (tourId === 'patientProfile') {
            setPendingTour('patientProfile');
            message.info(t('patientProfile.openPatientHint'));
            if (location.pathname !== '/patients') {
                navigate('/patients');
            }
            return;
        }
        if (tourId === 'patientLog') {
            setPendingTour('patientLog');
            message.info(t('patientLog.openAppointmentHint'));
            if (location.pathname !== '/appointments') {
                navigate('/appointments');
            }
            return;
        }
        var route = getTourRoute(tourId);
        if (!route) {
            return;
        }
        if (location.pathname === route) {
            window.setTimeout(function () { return startTour(tourId, { force: true }); }, 600);
            return;
        }
        navigate(route);
    }, [location.pathname, navigate, startTour, t, userId]);
    var replayAllTours = useCallback(function () {
        if (!userId) {
            return;
        }
        clearAllOnboarding(userId);
        chainToursRef.current = true;
        setShowWelcome(true);
    }, [userId]);
    useEffect(function () {
        if (isLoading ||
            !permissionsLoaded ||
            !userId ||
            welcomeCheckedRef.current) {
            return;
        }
        if (appConfig.IS_DEV) {
            var params = new URLSearchParams(location.search);
            if (params.get('onboardingReset') === '1') {
                clearAllOnboarding(userId);
                params.delete('onboardingReset');
                var nextSearch = params.toString();
                navigate({
                    pathname: location.pathname,
                    search: nextSearch ? "?".concat(nextSearch) : '',
                }, { replace: true });
            }
        }
        welcomeCheckedRef.current = true;
        var available = getAvailableTours();
        if (available.length === 0) {
            markWelcomeComplete(userId);
            return;
        }
        if (!isWelcomeComplete(userId)) {
            setShowWelcome(true);
        }
    }, [isLoading, location.pathname, location.search, navigate, permissionsLoaded, userId]);
    useEffect(function () {
        if (!userId || !isWelcomeComplete(userId) || tourRunningRef.current) {
            return;
        }
        var pendingTour = peekPendingTour();
        if (pendingTour === 'patientProfile' && isPatientProfilePath(location.pathname)) {
            consumePendingTour();
            var timer_1 = window.setTimeout(function () { return startTour('patientProfile', { force: true }); }, 600);
            return function () { return window.clearTimeout(timer_1); };
        }
        if (pendingTour === 'patientLog' && isShowAppointmentPath(location.pathname)) {
            consumePendingTour();
            var timer_2 = window.setTimeout(function () { return startTour('patientLog', { force: true }); }, 600);
            return function () { return window.clearTimeout(timer_2); };
        }
        var tourId = getTourForPath(location.pathname);
        if (!tourId || isTourComplete(userId, tourId)) {
            return;
        }
        var timer = window.setTimeout(function () {
            startTour(tourId, { chain: chainToursRef.current });
        }, 600);
        return function () { return window.clearTimeout(timer); };
    }, [location.pathname, startTour, userId]);
    var handleWelcomeStart = function () {
        if (!userId) {
            return;
        }
        markWelcomeComplete(userId);
        setShowWelcome(false);
        chainToursRef.current = true;
        var firstPending = getFirstPendingTour(userId);
        if (!firstPending) {
            return;
        }
        if (location.pathname === firstPending.route) {
            window.setTimeout(function () { return startTour(firstPending.tourId, { chain: true, force: true }); }, 600);
            return;
        }
        navigate(firstPending.route);
    };
    var handleWelcomeSkip = function () {
        if (!userId) {
            return;
        }
        markWelcomeComplete(userId);
        getAvailableTours().forEach(function (tourId) {
            markTourComplete(userId, tourId);
        });
        chainToursRef.current = false;
        setShowWelcome(false);
    };
    var availableTours = getAvailableTours();
    var contextValue = useMemo(function () { return ({
        availableTours: availableTours,
        replayTour: replayTour,
        replayAllTours: replayAllTours,
    }); }, [availableTours, replayAllTours, replayTour]);
    return (_jsxs(OnboardingContext.Provider, { value: contextValue, children: [children, _jsx(WelcomeModal, { open: showWelcome, availableTours: availableTours, onStart: handleWelcomeStart, onSkip: handleWelcomeSkip })] }));
};
export default OnboardingProvider;
